import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setBusiness, setStep, setUser } from "../../reducers/userReducer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import createAccount from "../../controllers/account/createAccount";
import updateAccount from "../../controllers/account/updateAccount";
import { enqueueSnackbar } from "notistack";

const DESCRIPTIONS = [
  { code: "Liscenced", label: "Licensed Agency" },
  { code: "SubAgent", label: "Sub-agent" },
  { code: "Independent", label: "Independent Entrepreneur" },
  { code: "Corporate", label: "Corporate Travel Manager" },
  { code: "Others", label: "Others (Not in travel)" },
];

const INTERESTS = [
  { code: "WhiteLabel", label: "White-label Website" },
  { code: "UseApi", label: "API Access" },
  { code: "GrowingSales", label: "Growing Sales" },
  { code: "AutomatingOperations", label: "Automating Operations" },
  { code: "TrainingAndSupport", label: "Training and Support" },
];

function LabeledCard({ label, children, optional, error, touched }) {
  const showError = touched && !!error;
  return (
    <div className="rounded-lg bg-white border border-gray-200 p-4 my-4">
      <div className="text-sm font-medium text-gray-800 mb-2">
        {label} {optional && <span className="text-gray-400">(Optional)</span>}
      </div>
      {children}
      {showError ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

function TextInput({ value, onChange, onBlur, placeholder, error, touched }) {
  const showError = touched && !!error;
  return (
    <input
      className={`w-full h-12 px-4 rounded-md border outline-none
                 placeholder:text-gray-400 text-gray-900 bg-white
                 ${
                   showError
                     ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                     : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                 }`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      type="text"
    />
  );
}

function Select({
  value,
  onChange, // will receive the selected code
  placeholder = "Select an option",
  options = [], // [{code, label}]
}) {
  const [open, setOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(-1);
  const wrapRef = useRef(null);

  // find label for current code
  const current = options.find((o) => o.code === value);
  const display = current?.label || "";

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const toggle = () => setOpen((o) => !o);

  const onKeyDown = (e) => {
    if (
      !open &&
      (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")
    ) {
      e.preventDefault();
      setOpen(true);
      setHoverIdx(
        Math.max(
          0,
          options.findIndex((o) => o.code === value)
        )
      );
      return;
    }
    if (open) {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHoverIdx((i) => (i + 1) % options.length);
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHoverIdx((i) => (i <= 0 ? options.length - 1 : i - 1));
      }
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        const opt = options[hoverIdx] ?? current;
        if (opt) onChange(opt.code);
        setOpen(false);
      }
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={toggle}
        onKeyDown={onKeyDown}
        className="w-full h-12 px-4 rounded-md border bg-white text-left
                   text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none
                   flex items-center justify-between"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={display ? "text-gray-900" : "text-gray-400"}>
          {display || placeholder}
        </span>
        <ExpandMoreIcon
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          fontSize="small"
        />
      </button>

      {open && (
        <div
          role="listbox"
          tabIndex={-1}
          className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg overflow-hidden"
        >
          {options.map((opt, idx) => {
            const selected = opt.code === value;
            const active = idx === hoverIdx;
            return (
              <button
                type="button"
                key={opt.code}
                role="option"
                aria-selected={selected}
                className={`w-full text-left px-4 py-3 text-sm
                  ${active ? "bg-blue-50" : "bg-white"}
                  ${selected ? "text-blue-700 font-medium" : "text-gray-800"}
                  hover:bg-blue-50`}
                onMouseEnter={() => setHoverIdx(idx)}
                onClick={() => {
                  onChange(opt.code);
                  setOpen(false);
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SegmentedBoolean({ value, onChange }) {
  const Item = ({ label, val }) => {
    const active = value === val;
    return (
      <button
        type="button"
        onClick={() => onChange(val)}
        className={`flex items-center justify-between gap-2 px-4 h-12 rounded-md border 
          ${active ? "border-blue-500 ring-2 ring-blue-100" : "border-gray-300"}
          bg-white text-gray-800 flex-1`}
      >
        <span>{label}</span>
        <span
          className={`w-4 h-4 rounded-full border 
            ${
              active
                ? "border-blue-600 bg-blue-600"
                : "border-gray-400 bg-transparent"
            }`}
        />
      </button>
    );
  };

  return (
    <div className="flex gap-2">
      <Item label="Yes" val={true} />
      <Item label="No" val={false} />
    </div>
  );
}

export default function ClaimBusinessSection() {
  const dispatch = useDispatch();
  const business = useSelector((s) => s.user.business);
  const basic = useSelector((s) => s.user.basic);
  const result = useSelector((s) => s.user.result);
  const createdUser = useSelector((s) => s.user.user);
  const [loading, setLoading] = useState(false);

  const [touched, setTouched] = useState({
    name: false,
    description: false,
    online: false,
    interest: false,
  });

  const [errors, setErrors] = useState({
    name: "",
    description: "",
    online: "",
    interest: "",
  });

  const setFieldTouched = (field) =>
    setTouched((p) => ({ ...p, [field]: true }));

  const hasValue = (v) =>
    v !== undefined && v !== null && String(v).trim().length > 0;

  const validateField = useCallback(
    (field, overrideVal) => {
      let msg = "";
      const val = overrideVal !== undefined ? overrideVal : business?.[field];

      switch (field) {
        case "name":
          if (!hasValue(val)) msg = "Business name is required.";
          break;
        case "description":
          if (!hasValue(val)) msg = "Please select a description.";
          break;
        case "online":
          if (val !== true && val !== false) msg = "Please choose Yes or No.";
          break;
        case "interest":
          if (!hasValue(val)) msg = "Please select an interest.";
          break;
        default:
          break;
      }
      setErrors((p) => ({ ...p, [field]: msg }));
      return msg;
    },
    [business]
  );

  const update = (field) => (value) => {
    dispatch(setBusiness({ field, value }));
    if (field === "name" && (touched.name || errors.name)) {
      validateField("name", value);
    }
  };

  const handleBlurName = () => {
    setFieldTouched("name");
    validateField("name", business.name);
  };

  const anyTouched = Object.values(touched).some(Boolean);
  const anyInvalidTouched = ["name", "description", "online", "interest"].some(
    (f) => touched[f] && !!errors[f]
  );
  const disableButton = anyTouched ? anyInvalidTouched : false;

  const onContinue = () => {
    const fields = ["name", "description", "online", "interest"];
    const nextTouched = { ...touched };
    fields.forEach((f) => (nextTouched[f] = true));
    setTouched(nextTouched);

    let hasErr = false;
    fields.forEach((f) => {
      const msg = validateField(f);
      if (msg) hasErr = true;
    });

    if (hasErr) return;

    // Example payload now sends codes for description/interest
    handleContinue();
    // console.log(payload);

    // dispatch(setStep(3));
  };

  const handleContinue = async () => {
    const payload = {
      firstName: basic.firstName,
      middleName: business?.name,
      lastName: basic.lastName,
      email: basic.email,
      phone: basic.phoneNumber,
      businessProfile: business?.description, // code
      needsAndGoals: [business?.interest], // code(s)
      sellsOnline: business?.online,
      numberOfBookings: "0-10",
      whenToGetStarted: "Immediately",
      remark: business?.challenge,
      reward: result?.id,
    };
    setLoading(true);
    const res = createdUser?._id
      ? await updateAccount(payload, createdUser?._id)?.finally(() => {
          setLoading(false);
        })
      : await createAccount(payload)?.finally(() => {
          setLoading(false);
        });
    const user = res?.data?.data;
    if (user) {
      dispatch(setUser(user));
      dispatch(setStep(3));
    } else {
      enqueueSnackbar(
        res?.msg || "Something went wrong. please try again later",
        { variant: "error" }
      );
    }
  };

  return (
    <section className="w-full">
      {/* Title */}
      <div className="pt-5">
        <h2 className="text-xl font-semibold">Step 2: Business Profile</h2>
      </div>

      {/* Business Name */}
      <LabeledCard
        label="Business Name"
        error={errors.name}
        touched={touched.name}
      >
        <TextInput
          value={business.name}
          onChange={update("name")}
          onBlur={handleBlurName}
          placeholder="Enter info here..."
          error={errors.name}
          touched={touched.name}
        />
      </LabeledCard>

      {/* What best describes you? (stores code) */}
      <LabeledCard
        label="What best describes you?"
        error={errors.description}
        touched={touched.description}
      >
        <Select
          value={business.description} // code
          onChange={update("description")} // will receive code
          placeholder="Select an option"
          options={DESCRIPTIONS} // {code,label}
        />
      </LabeledCard>

      {/* Do you currently sell travel online? */}
      <LabeledCard
        label="Do you currently sell travel online?"
        error={errors.online}
        touched={touched.online}
      >
        <SegmentedBoolean value={business.online} onChange={update("online")} />
      </LabeledCard>

      {/* What are you most interested in? (stores code) */}
      <LabeledCard
        label="What are you most interested in?"
        error={errors.interest}
        touched={touched.interest}
      >
        <Select
          value={business.interest} // code
          onChange={update("interest")} // will receive code
          placeholder="Select an option"
          options={INTERESTS} // {code,label}
        />
      </LabeledCard>

      {/* Biggest challenge (optional) */}
      <LabeledCard
        label="What’s your biggest challenge in selling travel today?"
        optional
      >
        <textarea
          className="w-full min-h-[96px] p-4 rounded-md border border-gray-300 outline-none
                     placeholder:text-gray-400 text-gray-900 bg-white
                     focus:border-blue-500 focus:ring-2 focus:ring-blue-100 resize-y"
          placeholder="Enter info here..."
          value={business.challenge}
          onChange={(e) => update("challenge")(e.target.value)}
        />
      </LabeledCard>

      {/* CTA */}
      <div className="py-5">
        <button
          onClick={onContinue}
          className={`w-full rounded-md py-3 text-white font-semibold bg-primary1 flex items-center justify-center shadow-sm active:scale-[.99]
            ${disableButton ? "opacity-60 cursor-not-allowed" : ""}`}
          disabled={disableButton || loading}
        >
          {loading ? <div className="buttonLoader"></div> : <>Continue</>}
        </button>
      </div>
    </section>
  );
}
